import React from "react";
import { useTranslation } from "react-i18next";
import FormButton from "reusableComponents/FormButton";
import {
  CopyIcon,
  ExternalLinkIcon,
  CalendarIcon,
  UserIcon,
  MapPinIcon,
  UsersIcon,
  EditIcon,
  DeleteIcon,
  ClockIcon
} from "../GoogleEventModal/svgIcons";
import styles from "./styles.module.scss";

const formatDateForDisplay = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
};

const formatTimeForDisplay = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const CustomBookingDetailsModal = ({
  booking = null,
  onClose = () => {},
  onDeleteSuccess = () => {},
  onDuplicate = () => {},
  onApprove = () => {},
  onReject = () => {},
}) => {
  const { t } = useTranslation("global");
  const [isProcessing, setIsProcessing] = React.useState(false);

  if (!booking) {
    return (
      <div className={styles.modal}>
        <div className={styles.errorMessage}>
          {t("calendar.bookingModal.noBookingData")}
        </div>
        <div className={styles.footer}>
          <FormButton
            label={t("buttons.close")}
            onClick={onClose}
            variant="secondary"
          />
        </div>
      </div>
    );
  }

  // FIXED: Extract data from the correct structure
  const extractBookingData = (booking) => {
    // 1. Customer data - Check multiple possible locations
    let customer = null;
    
    if (booking.user) {
      // Direct user object (from your booking data)
      customer = {
        firstName: booking.user.first_name || booking.user.firstName || "",
        lastName: booking.user.last_name || booking.user.lastName || "",
        email: booking.user.email || "",
        phone: booking.user.phone || "",
        company: booking.user.social_reason || booking.user.company || "",
      };
    } else if (booking.extendedProps?.customer) {
      // From extendedProps
      customer = {
        firstName: booking.extendedProps.customer.firstName || "",
        lastName: booking.extendedProps.customer.lastName || "",
        email: booking.extendedProps.customer.email || "",
        phone: booking.extendedProps.customer.phone || "",
        company: booking.extendedProps.customer.company || "",
      };
    }

    // 2. Place data
    const place = booking.place || booking.extendedProps?.place || null;
    
    // 3. Booking times - Handle multiple possible formats
    let startDate, endDate, startTime, endTime;
    
    if (booking.start_date && booking.end_date) {
      // Direct date fields
      startDate = new Date(booking.start_date);
      endDate = new Date(booking.end_date);
    } else if (booking.start && booking.end) {
      // Event date objects
      startDate = new Date(booking.start);
      endDate = new Date(booking.end);
    } else if (booking.booking_days && booking.booking_days.length > 0) {
      // From booking_days structure
      const firstDay = booking.booking_days[0];
      if (firstDay.booking_hours && firstDay.booking_hours.length > 0) {
        const firstHour = firstDay.booking_hours[0];
        startDate = new Date(`${firstDay.date} ${firstHour.from}`);
        endDate = new Date(`${firstDay.date} ${firstHour.to}`);
      }
    }

    // 4. Capacity - Check multiple locations
    const capacity = 
      booking.capacity || 
      booking.extendedProps?.capacity || 
      (booking.place?.desk ? booking.place.desk : 0);

    // 5. Services
    const services = booking.services || booking.extendedProps?.services || [];

    // 6. Pricing
    const total = booking.total || 0;
    const vat = booking.vat || 0;
    const totalWithVat = booking.total_with_vat || booking.totalWithVat || total + vat;

    // 7. Status
    const status = booking.status || booking.extendedProps?.status || "waiting";

    return {
      id: booking.id || "",
      title: booking.title || place?.name || t("calendar.bookingModal.noTitle"),
      startDate,
      endDate,
      startTime: startDate ? formatTimeForDisplay(startDate) : "",
      endTime: endDate ? formatTimeForDisplay(endDate) : "",
      capacity,
      coworkingStations: booking.coworking_stations || booking.extendedProps?.coworking_stations || 0,
      place,
      customer: customer || {
        firstName: t("calendar.bookingModal.labels.notAvailable"),
        lastName: "",
        email: t("calendar.bookingModal.labels.notAvailable"),
        phone: t("calendar.bookingModal.labels.notAvailable"),
        company: t("calendar.bookingModal.labels.notAvailable"),
      },
      status,
      priority: booking.priority || booking.extendedProps?.priority || "normal",
      services,
      total,
      vat,
      totalWithVat,
      bookingDays: booking.booking_days || [],
      rawData: booking, // Keep raw data for debugging
    };
  };

  // Get processed booking data
  const bookingData = extractBookingData(booking);

  const handleCopyDetails = () => {
    const details = [
      `${t("calendar.bookingModal.copy.booking")}: ${bookingData.title}`,
      `${t("calendar.bookingModal.copy.status")}: ${t(`calendar.bookingModal.status.${bookingData.status}`)}`,
      `${t("calendar.bookingModal.copy.date")}: ${formatDateForDisplay(bookingData.startDate)}`,
      `${t("calendar.bookingModal.copy.time")}: ${bookingData.startTime} - ${bookingData.endTime}`,
      `${t("calendar.bookingModal.copy.location")}: ${bookingData.place?.name || t("calendar.bookingModal.labels.notAvailable")}`,
      `${t("calendar.bookingModal.copy.customer")}: ${bookingData.customer.firstName} ${bookingData.customer.lastName}`,
      `${t("calendar.bookingModal.copy.company")}: ${bookingData.customer.company}`,
      `${t("calendar.bookingModal.copy.email")}: ${bookingData.customer.email}`,
      `${t("calendar.bookingModal.copy.phone")}: ${bookingData.customer.phone}`,
      `${t("calendar.bookingModal.copy.capacity")}: ${bookingData.capacity} ${t(`calendar.bookingModal.labels.${bookingData.coworkingStations > 0 ? 'stations' : 'people'}`)}`,
      `${t("calendar.bookingModal.copy.total")}: ${bookingData.totalWithVat} €`
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(details)
      .then(() => alert(t("calendar.bookingModal.alerts.copySuccess")))
      .catch(() => alert(t("calendar.bookingModal.alerts.copyError")));
  };

  const handleApprove = async () => {
    setIsProcessing(true);
    try {
      await onApprove(bookingData.id);
      onDeleteSuccess();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    setIsProcessing(true);
    try {
      await onReject(bookingData.id);
      onDeleteSuccess();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDuplicate = () => {
    onDuplicate();
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.eventTypeBadge}>
            <span className={styles.bookingLogo}>C</span>
            {t("calendar.bookingModal.badge")}
          </div>
          <h2 className={styles.title}>{bookingData.title}</h2>
          <button onClick={onClose} className={styles.closeButton} aria-label={t("calendar.bookingModal.buttons.close")}>
            &times;
          </button>
        </div>

        <div className={styles.content}>
          {/* Status */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <ClockIcon className={styles.sectionIcon} />
              {t("calendar.bookingModal.sections.status")}
            </h3>
            <div className={styles.statusBadge} data-status={bookingData.status}>
              {t(`calendar.bookingModal.status.${bookingData.status}`)}
            </div>
          </div>

          {/* Date and Time Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <CalendarIcon className={styles.sectionIcon} />
              {t("calendar.bookingModal.sections.dateTime")}
            </h3>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("calendar.bookingModal.labels.date")}</span>
                <span className={styles.detailValue}>
                  {formatDateForDisplay(bookingData.startDate)}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("calendar.bookingModal.labels.start")}</span>
                <span className={styles.detailValue}>
                  {bookingData.startTime}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("calendar.bookingModal.labels.end")}</span>
                <span className={styles.detailValue}>
                  {bookingData.endTime}
                </span>
              </div>
            </div>
          </div>

          {/* Location Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <MapPinIcon className={styles.sectionIcon} />
              {t("calendar.bookingModal.sections.location")}
            </h3>
            {bookingData.place ? (
              <div className={styles.locationCard}>
                <div className={styles.locationName}>{bookingData.place.name}</div>
                {bookingData.place.address && (
                  <div className={styles.locationAddress}>
                    {bookingData.place.address.address || bookingData.place.address}
                    {bookingData.place.address.city && `, ${bookingData.place.address.city}`}
                    {bookingData.place.address.country && `, ${bookingData.place.address.country}`}
                  </div>
                )}
                {bookingData.place.lat && bookingData.place.lng && (
                  <button 
                    className={styles.mapLink} 
                    onClick={() => {
                      window.open(
                        `https://www.google.com/maps?q=${bookingData.place.lat},${bookingData.place.lng}`,
                        '_blank'
                      );
                    }}
                  >
                    <ExternalLinkIcon className={styles.linkIcon} />
                    {t("calendar.bookingModal.buttons.viewOnMap")}
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.noData}>
                {t("calendar.bookingModal.labels.noLocation")}
              </div>
            )}
          </div>

          {/* Customer Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <UserIcon className={styles.sectionIcon} />
              {t("calendar.bookingModal.sections.customer")}
            </h3>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("calendar.bookingModal.labels.name")}</span>
                <span className={styles.detailValue}>
                  {bookingData.customer.firstName} {bookingData.customer.lastName}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("calendar.bookingModal.labels.company")}</span>
                <span className={styles.detailValue}>{bookingData.customer.company}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("calendar.bookingModal.labels.email")}</span>
                <span className={styles.detailValue}>
                  {bookingData.customer.email !== t("calendar.bookingModal.labels.notAvailable") ? (
                    <a href={`mailto:${bookingData.customer.email}`}>{bookingData.customer.email}</a>
                  ) : (
                    <span className={styles.noDataText}>{bookingData.customer.email}</span>
                  )}
                </span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>{t("calendar.bookingModal.labels.phone")}</span>
                <span className={styles.detailValue}>
                  {bookingData.customer.phone !== t("calendar.bookingModal.labels.notAvailable") ? (
                    <a href={`tel:${bookingData.customer.phone}`}>{bookingData.customer.phone}</a>
                  ) : (
                    <span className={styles.noDataText}>{bookingData.customer.phone}</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Capacity Section */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
              <UsersIcon className={styles.sectionIcon} />
              {bookingData.coworkingStations > 0 
                ? t("calendar.bookingModal.sections.coworkingStations") 
                : t("calendar.bookingModal.sections.capacity")}
            </h3>
            <div className={styles.capacityBadge}>
              <span className={styles.capacityNumber}>
                {bookingData.capacity > 0 ? bookingData.capacity : 
                 bookingData.coworkingStations > 0 ? bookingData.coworkingStations : 
                 t("calendar.bookingModal.labels.notSpecified")}
              </span>
              <span className={styles.capacityLabel}>
                {t(`calendar.bookingModal.labels.${bookingData.coworkingStations > 0 ? 'stations' : 'people'}`)}
              </span>
            </div>
          </div>

          {/* Services Section */}
          {bookingData.services && bookingData.services.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <CalendarIcon className={styles.sectionIcon} />
                {t("calendar.bookingModal.sections.services")}
              </h3>
              <div className={styles.servicesList}>
                {bookingData.services.map((service, index) => (
                  <div key={index} className={styles.serviceItem}>
                    <span>{service.name || `Service ${index + 1}`}</span>
                    {service.quantity && service.price && (
                      <span>{service.quantity} x {service.price} €</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pricing Section */}
          {bookingData.total > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>
                <CalendarIcon className={styles.sectionIcon} />
                {t("calendar.bookingModal.sections.pricing")}
              </h3>
              <div className={styles.pricingDetails}>
                {bookingData.total > 0 && (
                  <div className={styles.pricingRow}>
                    <span>{t("calendar.bookingModal.labels.subtotal")}:</span>
                    <span>{bookingData.total} €</span>
                  </div>
                )}
                {bookingData.vat > 0 && (
                  <div className={styles.pricingRow}>
                    <span>{t("calendar.bookingModal.labels.vat")}:</span>
                    <span>{bookingData.vat} €</span>
                  </div>
                )}
                {bookingData.totalWithVat > 0 && (
                  <div className={styles.pricingRowTotal}>
                    <span>{t("calendar.bookingModal.labels.total")}:</span>
                    <span>{bookingData.totalWithVat} €</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.secondaryButton} onClick={handleCopyDetails}>
            <CopyIcon className={styles.buttonIcon} />
            {t("calendar.bookingModal.buttons.copyDetails")}
          </button>
          
          <div className={styles.actionButtons}>
            {bookingData.status === "waiting" && (
              <>
                <button 
                  className={styles.deleteButton} 
                  onClick={handleReject} 
                  disabled={isProcessing}
                >
                  <DeleteIcon className={styles.buttonIcon} />
                  {isProcessing ? t("calendar.bookingModal.buttons.processing") : t("calendar.bookingModal.buttons.reject")}
                </button>
                <button 
                  className={styles.primaryButton} 
                  onClick={handleApprove}
                  disabled={isProcessing}
                >
                  <EditIcon className={styles.buttonIcon} />
                  {isProcessing ? t("calendar.bookingModal.buttons.processing") : t("calendar.bookingModal.buttons.approve")}
                </button>
              </>
            )}
            {bookingData.status !== "waiting" && (
              <button className={styles.primaryButton} onClick={onClose}>
                {t("calendar.bookingModal.buttons.close")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomBookingDetailsModal;